import { Check } from "lucide-react";
import { useIntl } from "react-intl";

import { useDomainColumns } from "../../../../evidence/domain";
import { Tag } from "../../../atoms/tag";
import { DataFormToolbarButton } from "../../../data-form";
import { TypedColumns } from "../../columns";
import { useDataFormPreferences } from "./context";
import { useGetProfileFromPreferences } from "./hooks/get-profile-from-preference";
import { type DataTablePreferencesBrowseType } from "./schema";

const { tableColumn } = new TypedColumns<DataTablePreferencesBrowseType>();

function useIdColumn() {
  const intl = useIntl();

  return tableColumn.text({
    name: "id",
    label: intl.formatMessage({
      id: "data-table.preferences.columns.id.label",
      defaultMessage: "Id",
    }),
  });
}

function useNameColumn() {
  const intl = useIntl();

  const profile = useGetProfileFromPreferences();

  return tableColumn.text({
    id: "name",
    label: intl.formatMessage({
      id: "data-table.preferences.columns.name.label",
      defaultMessage: "Název",
    }),
    enableGlobalFilter: true,
    cell: ({ row }) => {
      return (
        <div className="flex max-h-[1lh] items-center gap-x-1">
          <span className="truncate">{row.original.name}</span>
          {profile?.id === row.original.id && (
            <Tag inverse={true} className="max-h-[inherit] min-h-0" size="xs">
              {intl.formatMessage({
                id: "data-table.preferences.columns.name.active",
                defaultMessage: "Aktivní",
              })}
            </Tag>
          )}
        </div>
      );
    },
  });
}

function useActionsColumn() {
  const intl = useIntl();

  const profile = useGetProfileFromPreferences();

  const { setMode, setEntity } = useDataFormPreferences();

  return tableColumn.actions({
    label: intl.formatMessage({
      id: "data-table.preferences.columns.actions.label",
      defaultMessage: "Akce",
    }),
    actions: (row) => {
      if (profile?.id === row.id) {
        return null;
      }

      return (
        <DataFormToolbarButton
          icon={{ Icon: Check }}
          tooltip={intl.formatMessage({
            id: "data-table.preferences.columns.actions.select-profile",
            defaultMessage: "Použít nastavení",
          })}
          onClick={(e) => {
            e.stopPropagation();

            setEntity(row);
            setMode("apply");
          }}
        />
      );
    },
  });
}

export function useDataTablePreferencesColumns() {
  const idColumn = useIdColumn();
  const nameColumn = useNameColumn();

  const domainColumns = useDomainColumns<DataTablePreferencesBrowseType>({
    createdBy: false,
    modifiedBy: false,
    modifiedAt: false,
  });

  const actionsColumn = useActionsColumn();

  return [idColumn, nameColumn, ...domainColumns, actionsColumn];
}
